import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useCreatorsList } from '../../../hooks/useCreatorsList';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import CreatorsFilterBar from '../../../components/creators-v2/CreatorsFilterBar';
import CreatorsListTable from '../../../components/creators-v2/CreatorsListTable';

/**
 * Admin V2 Creators — Liste paginée + filtrée des créateurs (admin scope).
 * Route : /admin/creators (via V2Switcher) + /admin/v2/creators.
 * Feature flag : `pages.creators.list_admin`.
 */
const AdminV2Creators = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '', server: '', tier: '', managerId: '' });

  const { rows, managers, loading, error, reload } = useCreatorsList({
    managerId: filters.managerId || null,
    includeDeparted: false,
  });

  const setFilter = (field, value) => setFilters((f) => ({ ...f, [field]: value }));

  // Derived option lists
  const servers = useMemo(() => Array.from(new Set(rows.map((r) => r.server).filter(Boolean))).sort(), [rows]);
  const tiers = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      if (r.tier_current != null) s.add(String(r.tier_current));
      else if (r.tier != null) s.add(String(r.tier));
    }
    return Array.from(s).sort();
  }, [rows]);

  // Client-side filtering
  const filtered = useMemo(() => {
    const term = filters.search?.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.status && r.status !== filters.status) return false;
      if (filters.server && r.server !== filters.server) return false;
      if (filters.tier && String(r.tier_current ?? r.tier ?? '') !== filters.tier) return false;
      if (term) {
        const hay = `${r.display_name || ''} ${r.tiktok_username || ''} ${r.whatsapp_phone || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4">
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
              {t('creatorsV2.adminTitle', 'Creators')}
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5">
              {tenantName} · {filtered.length} / {rows.length}
            </p>
          </div>
        </header>

        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{String(error.message || error)}</span>
              <button type="button" onClick={reload} className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium">
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          <CreatorsFilterBar
            filters={filters}
            servers={servers}
            tiers={tiers}
            managers={managers}
            showManagerFilter={true}
            onChange={setFilter}
          />

          <CreatorsListTable data={filtered} loading={loading} basePath="/admin/v2/creators" />
        </div>
      </main>
    </div>
  );
};

export default AdminV2Creators;
